import { useContext, useEffect, useMemo, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import { TableContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/TableContext'
import { assetUrl } from 'utils/url'

export const CONFIRMATION_TEXT =
  'SAYA SETUJU UNTUK MENGIRIM KE PENGAWAS SEKOLAH'

export type ActionBulkSendToEkinOptions = {
  onSuccess?: () => void
  onError?: () => void
}

type ActionBulkSendToEkinProps = {
  isDisabled?: boolean
  isSubmitting?: boolean
  count?: number
  onSubmit?: (options?: ActionBulkSendToEkinOptions) => void
  onCloseSuccessDialog?: () => void
}

export function ActionBulkSendToEkin({
  isDisabled: disabled,
  isSubmitting,
  count = 0,
  onSubmit,
  onCloseSuccessDialog,
}: ActionBulkSendToEkinProps) {
  const context = useContext(TableContext)

  // Ensure that the component is used within the correct context provider
  if (!context)
    throw new Error(
      '<TablePegawaiPenetapan.ActionBulkSendToEkin> must be used within a <TablePegawaiPenetapan.Provider>'
    )

  const isDisabled = useMemo(() => {
    return disabled || count === 0
  }, [disabled, count])

  // --------------------------
  // Handle confirmation dialog
  // --------------------------
  const [step, setStep] = useState<
    'CONFIRMATION' | 'SUBMITTING' | 'SUCCESS' | 'IDLE'
  >('IDLE')
  const handleClose = () => {
    // Hide confirmation dialog
    setStep('IDLE')
  }
  const handleCloseAndReset = () => {
    handleClose()
    onCloseSuccessDialog?.()
  }
  const handleOpen = () => {
    // Show confirmation dialog
    setStep('CONFIRMATION')
  }

  // ------------------
  // Handle submission
  // ------------------
  const handleSubmit = () => {
    onSubmit?.({
      onSuccess: () => {
        // Show success dialog
        setStep('SUCCESS')
      },
      onError: () => setStep('IDLE'),
    })
  }

  useEffect(() => {
    if (isSubmitting) {
      setStep('SUBMITTING')
    }
  }, [isSubmitting])

  return (
    <>
      <Button
        className="gap-x-1 h-fit !text-sm"
        disabled={isDisabled}
        onClick={handleOpen}
      >
        <Icon color={isDisabled ? 'disabled' : 'inverse'} fontSize="sm">
          mail_outline
        </Icon>
        Lanjut kirim {count ? `(${count})` : ''}
      </Button>

      <Dialog
        isOpen={['CONFIRMATION', 'SUBMITTING', 'SUCCESS'].includes(step)}
        closeOnOverlayClick={false}
        size="sm"
        onClose={handleCloseAndReset}
      >
        <DialogOverlay />
        <DialogContent>
          {step === 'CONFIRMATION' && (
            <StepConfirmation onNext={handleSubmit} onPrev={handleClose} />
          )}

          {step === 'SUBMITTING' && <StepSending />}

          {step === 'SUCCESS' && (
            <StepSuccess count={count} onConfirm={handleCloseAndReset} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

type StepConfirmationProps = {
  isLoading?: boolean
  onPrev?: () => void
  onNext?: () => void
}
function StepConfirmation({
  isLoading,
  onPrev,
  onNext,
}: StepConfirmationProps) {
  const [confirmation, setConfirmation] = useState('')

  const isEnabled = (confirmation ?? '').toUpperCase() === CONFIRMATION_TEXT

  return (
    <div className="p-6 space-y-4">
      <img
        src={assetUrl('/illustrations/img-publish-data.png')}
        alt=""
        width={84}
        height={84}
      />

      <Text variant="display-sm" as="h2">
        Konfirmasi untuk Kirim Predikat Kinerja
      </Text>

      <Text variant="body-lg" color="subdued" className="mt-2">
        Data tidak dapat diubah lagi. Pengawas Sekolah akan menerima Hasil
        Penilaian Kinerja.
      </Text>
      <div className="mt-4">
        <Text color="subdued" as="label" htmlFor="confirmation-text">
          Mohon ketik kalimat ini pada kolom kalimat persetujuan:
        </Text>
        <div className="p-1 mt-3 rounded select-none bg-blue-5 text-blue-60">
          <Text variant="heading-sm" color="inherit">
            {CONFIRMATION_TEXT}
          </Text>
        </div>
      </div>
      <div className="max-w-[70%]">
        <Text>Kalimat persetujuan</Text>
        <Textarea
          className="mt-1 uppercase"
          placeholder="Ketik di sini..."
          autoComplete="off"
          value={confirmation}
          onChange={(e) => setConfirmation(e?.target?.value)}
        />
      </div>

      <div className="flex space-x-3 !justify-start">
        <Button
          color="white"
          size="lg"
          disabled={isLoading}
          onClick={onPrev}
          className="w-40"
        >
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!isEnabled || isLoading}
          isLoading={isLoading}
          size="lg"
          type="button"
          onClick={() => onNext?.()}
          className="w-40"
        >
          Konfirmasi
        </Button>
      </div>
    </div>
  )
}

type StepSendingProps = {}
function StepSending({}: StepSendingProps) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-9">
      <div
        className="mt-6"
        style={
          {
            '--lg-size': '72px',
            '--lg-border-size': '16px',
          } as React.CSSProperties
        }
      >
        <Spinner size="lg" />
      </div>
      <div className="text-center mt-9 w-96">
        <Text variant="display-sm">Mengirim ke Pengawas Sekolah...</Text>
        <Text variant="body-lg" color="subdued">
          Mohon tunggu sebentar, ya.
        </Text>
      </div>
    </div>
  )
}

type StepSuccessProps = {
  count: number
  onConfirm: () => void
}
function StepSuccess({ count, onConfirm }: StepSuccessProps) {
  return (
    <div className="p-6">
      <img
        src={assetUrl('/illustrations/img-thumbs-up.png')}
        alt=""
        width={84}
        height={84}
        className="mb-4"
      />

      <div className="mb-6">
        <Text variant="display-sm" as="h2" className="mb-2">
          {count} Penetapan Predikat Kinerja berhasil dikirim ke Pengawas
          Sekolah
        </Text>

        <Text variant="body-lg" color="subdued">
          Anda dapat melanjutkan pengiriman jika ada yang belum dikirim. Mohon
          cek secara berkala.
        </Text>
      </div>

      <Button
        color="blue"
        size="lg"
        type="button"
        onClick={onConfirm}
        className="w-48"
      >
        Saya mengerti
      </Button>
    </div>
  )
}
